import { createStyles, makeStyles, Theme } from "@material-ui/core";
import axios from "axios";
import ElementSizes from "Constants/ElementSizes";
import LanguageCodes from "Constants/LanguageCodes";
import { RouteParams } from "Models/RouteParams";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import React, {FC, useCallback, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {useAuth} from "../../../auth";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    powerBiFrame: {
      width: "100%",
      height: `calc(100% - ${ElementSizes.LOGO_HEIGHT}px)`,
    },
    reportTitle: {
      marginBottom: theme.spacing(1),
    },
  }),
);

export const BiReport: FC = () => {
  const classes = useStyles();
  const auth = useAuth();
  const { tag } = useParams<RouteParams>();
  const { i18n } = useTranslation();

  const [data, setData] = useState<any>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);

  const getPowerBiLocale = (language: string): string => {
    return language === LanguageCodes.PORTUGUESE ? "pt-PT" : "en-GB";
  };

  const currentLocale = getPowerBiLocale(i18n.language);

  const [reportConfig, setReportConfig] =
    useState<models.IReportEmbedConfiguration>({
      type: "report",
      id: undefined,
      embedUrl: undefined,
      accessToken: undefined,
      tokenType: models.TokenType.Embed,
    });

  const getReport = useCallback(async () => {
    const token = await auth.auth?.getToken();
    // Add config to axios request when API is fully deployed
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    // Temporarily call the legacy endpoint when no report metadata was used to navigate to this page
    const reportTag = tag === "anpg" ? "" : tag;

    axios
      .get(
        `${process.env.REACT_APP_FILE_SERVER_URL}/powerbi/embedreport/${reportTag}`,
        config,
      )
      .then((response: any) => {
        setData(response.data);
      })
      .catch((error: any) => {
        console.warn(error);
      });
    // eslint-disable-next-line
  }, [tag]);

  useEffect(() => {
    getReport();
    // eslint-disable-next-line
  }, [tag]);

  useEffect(() => {
    if (data) {
      const embedUrlWithLocale = `${data.embedReport.embedUrl}?language=${currentLocale}&formatLocale=${currentLocale}`;

      setReportConfig({
        type: "report",
        tokenType: models.TokenType.Embed,
        embedUrl: embedUrlWithLocale,
        accessToken: data.embedToken.token,
        id: data.embedReport.reportId,
        settings: {
          panes: {
            filters: {
              visible: false,
            },
          },
          localeSettings: {
            language: currentLocale,
            formatLocale: currentLocale,
          },
          background: models.BackgroundType.Default,
        },
      });
      setEmbedUrl(embedUrlWithLocale);
    }
  }, [currentLocale, data]);

  return (
    <PowerBIEmbed
      key={embedUrl}
      cssClassName={classes.powerBiFrame}
      embedConfig={reportConfig}
    />
  );
};
